import React, { useEffect, useState } from "react";
import { FaEye } from "react-icons/fa";
import useEscClose from "../../useEscClose";
import apiRoutes from "../../../api/apiRoutes";
import { appFetchJson } from "../../../utils/http";
import { shouldRunOnce } from "../../../utils/devOnce";
import { formatoPy } from "../../../utils/formatoNumeros";
import { getActorType } from "../../../utils/auth";

// Componentes de diseño
import ListHeader from "../../../components/listing/ListHeader";
import ListFooter from "../../../components/listing/ListFooter";
import DynamicTable from "../../../components/listing/DynamicTable";

// Componentes hijos
import FiltroRemitos from "./FiltroRemitos"; 
import VerRemito from "./VerRemito.jsx";

const DEFAULT_PAGE_SIZE = 30;

export default function ListarRemitos() {
  // ===========================
  //       ESTADOS Y DATA
  // ===========================
  const [remitos, setRemitos] = useState([]);
  const [cargando, setCargando] = useState(true);

  const [paginaActual, setPaginaActual] = useState(1);
  const [porPagina, setPorPagina] = useState(DEFAULT_PAGE_SIZE);
  const [totalBackend, setTotalBackend] = useState(0);

  // Filtros
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const [filtros, setFiltros] = useState({});
  useEscClose(() => setMostrarFiltros(false), mostrarFiltros);

  // Ver detalle
  const [detalleData, setDetalleData] = useState(null);
  useEscClose(() => setDetalleData(null), detalleData != null);

  const esCliente = getActorType() === "CLIENTE";
  // ===========================
  //       FETCH LISTADO
  // ===========================
  const fetchList = async (
    pageIndex = paginaActual - 1,
    size = porPagina,
    filtrosAplicados = filtros
  ) => {
    setCargando(true);
    try {
      const url = apiRoutes.transacciones.listUrl({
        page: pageIndex,
        size: size,
        ...filtrosAplicados
      });
      
      const data = await appFetchJson(url);

      const items = data?.items || data?.content || [];
      const total = data?.totalElements ?? data?.total ?? items.length;

      setRemitos(items);
      setTotalBackend(total);
    } catch (err) {
      console.error("Error al obtener remitos:", err);
      setRemitos([]);
      setTotalBackend(0);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    if (!shouldRunOnce("ListarRemitos:init")) return;
    fetchList(0, porPagina, filtros);
    // eslint-disable-next-line
  }, []);

  // ===========================
  //       ACCIONES
  // ===========================
  const cambiarPagina = (num) => {
    setPaginaActual(num);
    fetchList(num - 1, porPagina, filtros);
  };

  const manejarCambioCantidad = (size) => {
    setPorPagina(size);
    setPaginaActual(1);
    fetchList(0, size, filtros);
  };

  // ====================================================================
  // DEFINICIÓN DE COLUMNAS (REAJUSTE ANTI-COLISIÓN 1210px)
  // Estrategia: Usar 'text-xs' para datos densos y 'break-all' para seguridad.
  // ====================================================================
  const columns = [
    // 1. NRO. REMITO (10%) - Aumentado
    { 
      header: "Nro. Remito", 
      accessor: "codigo",
      type: "text", 
      width: "10%", 
      cellClass: "font-medium text-gray-800 text-xs break-all" 
    },

    // 2. NRO. BOLETA (10%) - Aumentado
    { 
      header: "Nro. Boleta", 
      accessor: "numeroBoleta",
      type: "text", 
      width: "10%", 
      cellClass: "text-gray-600 text-xs break-all"
    },

    // 3. VALOR VENTA (11%) - Aumentado
    { 
      header: "Valor Venta", 
      type: "center", 
      width: "11%", 
      cellClass: "text-center font-mono font-bold text-gray-700 text-xs", 
      render: (row) => formatoPy(row.valorVenta)
    },

    // 4. FECHA (10%)
    { 
      header: "Fecha", 
      accessor: "dataFecha",
      type: "center", 
      width: "10%",
      cellClass: "text-xs"
    },

    // 5. TIPO (9%) - Reducido levemente
    { 
      header: "Tipo", 
      accessor: "descricaoTipo",
      type: "text", 
      width: "10%",
      cellClass: "text-xs text-gray-600 break-words"
    },

    // 6. CLIENTE (16%) - Reducido (Se ajustará en varias líneas)
    { 
      header: "Cliente", 
      accessor: "clienteNombre",
      type: "text", 
      width: "15%",
      cellClass: "whitespace-normal break-words leading-tight font-medium text-xs"
    },

    // 7. EESS (14%) - Reducido
    { 
      header: "EESS", 
      accessor: "eessNombre",
      type: "text", 
      width: "14%",
      cellClass: "whitespace-normal break-words leading-tight text-gray-600 text-xs"
    },

    // 8. TARJETA (12%) - Aumentado (Los números son largos)
    { 
      header: "Tarjeta", 
      accessor: "tarjetaNumero",
      type: "center", 
      width: "12%",
      cellClass: "font-mono text-xs text-gray-500 break-all"
    },

    // 9. ACCIÓN (8%)
    {
      header: "Acción",
      type: "action", 
      width: "8%",
      render: (row) => (
        <div className="flex justify-center items-center">
          <button 
            className="text-gray-500 hover:text-blue-600 p-1" 
            title="Visualizar y Ver Detalles"
            onClick={() => setDetalleData(row)}
          >
            <FaEye size={18} />
          </button>
        </div>
      )
    }
  ];

  // ===========================
  //       RENDER
  // ===========================
  return (
    <div className="page">

      <ListHeader
        title="Transacciones (Remitos)"
        // Filtros
        filters={filtros}
        onFilterClick={() => setMostrarFiltros(!mostrarFiltros)} 
        onClearFilters={() => {
            setFiltros({});
            setPaginaActual(1);
            fetchList(0, porPagina, {});
        }}
        // Paginación
        pageSize={porPagina}
        onPageSizeChange={manejarCambioCantidad}
        // No hay botón de Agregar
        showAdd={false}
      />

      {/* FILTROS DRAWER */}
      {mostrarFiltros && (
        <FiltroRemitos
          initialFilters={filtros}
          onSubmit={(dataF) => {
            setFiltros(dataF);
            setPaginaActual(1);
            setMostrarFiltros(false);
            fetchList(0, porPagina, dataF);
          }}
          onClose={() => setMostrarFiltros(false)}
        />
      )}

      {/* CARGANDO */}
      {cargando && (
        <div className="mt-2 text-sm text-center text-gray-600">Cargando remitos...</div>
      )}

      {/* TABLA DINÁMICA */}
      {!cargando && (
        <DynamicTable 
          size="L" // Usa tarjetas móviles solo si baja de 1210px
          data={remitos}
          columns={columns}
        />
      )}

      {/* FOOTER PAGINACIÓN */}
      <ListFooter
        page={paginaActual}
        pageSize={porPagina}
        total={totalBackend}
        onPageChange={cambiarPagina}
      />

      {/* MODAL DETALLE */}
      {detalleData != null && (
        <div className="modal-backdrop" onClick={() => setDetalleData(null)}>
          <div className="modal-panel-lg" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setDetalleData(null)}
              className="modal-close-x"
            >
              &times;
            </button>
            <VerRemito data={detalleData} onClose={() => setDetalleData(null)} esCliente={esCliente} />
          </div>
        </div>
      )}
    </div>
  );
}